import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { initSentry, loadReplayLater } from "./utils/sentry";
import { initPostHog } from "./utils/posthog";
import { initClarity } from "./utils/clarity";

// Bootstrap sequence:
//   1. Sentry CORE (error capture) — eager. We try /api/config/public first
//      (so publishable IDs come from the backend and can be rotated at
//      runtime), but if that call is slow or fails we FALL BACK to build-time
//      env vars so error capture is NEVER lost. See _initSentryWithFallback.
//   2. Sentry REPLAY + PostHog SDK — deferred until AFTER the page `load`
//      event (via requestIdleCallback with a setTimeout fallback) so they
//      never block first paint. PostHog calls made in the meantime are
//      buffered by src/utils/posthog.js and flushed automatically once
//      loaded.
//   3. Any users who bounce BEFORE `load` fires do not get captured in
//      PostHog. Accepted trade-off for the ~62 KB gz SDK not shipping on the
//      critical path — flag on the perf dashboard if bounce-rate rows drop.
//   4. Config-fetch failure — beacon to the backend so the blind spot is
//      logged (Phase 7 addition, preserved here).

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// Build-time fallbacks. Populate via /app/frontend/.env at build time when
// runtime config-fetch cannot be relied on (early boot errors, backend down).
// These are ONLY consulted if /api/config/public fails or times out.
const BUILD_FALLBACK = {
  sentry_frontend_dsn: process.env.REACT_APP_SENTRY_DSN || "",
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "development",
  release: process.env.REACT_APP_SENTRY_RELEASE || "",
  posthog_js_key: process.env.REACT_APP_POSTHOG_KEY || "",
  posthog_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
};

const CONFIG_TIMEOUT_MS = 3000;

function _scheduleDeferred(cfg) {
  const runDeferred = () => {
    loadReplayLater();
    initPostHog({ apiKey: cfg.posthog_js_key, apiHost: cfg.posthog_host });
    // Microsoft Clarity — same deferred pattern (queue-shim + idle-callback)
    // so the ~40 KB SDK stays off the critical path. `clarity_project_id`
    // comes from /api/config/public (publishable — allowlisted).
    initClarity({ projectId: cfg.clarity_project_id });
  };
  const schedule = () => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runDeferred, { timeout: 3000 });
    } else {
      setTimeout(runDeferred, 1500);
    }
  };
  if (typeof document !== 'undefined' && document.readyState === 'complete') {
    schedule();
  } else if (typeof window !== 'undefined') {
    window.addEventListener('load', schedule, { once: true });
  } else {
    runDeferred();
  }
}

async function _fetchPublicConfigWithTimeout() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CONFIG_TIMEOUT_MS);
  try {
    const res = await fetch(`${backendUrl}/api/config/public`, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) throw new Error(`config/public ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

(async () => {
  // (1) Fire Sentry CORE from BUILD-TIME fallback IMMEDIATELY — this is the
  //     only line that guarantees error capture works even if /api/config/public
  //     never returns. If runtime config later provides different values (e.g.
  //     an admin-rotated DSN), we overwrite by re-calling initSentry — the
  //     `_active` guard in utils/sentry.js prevents double-init, so a runtime
  //     value simply upgrades the fallback if the DSN differs.
  initSentry({
    dsn: BUILD_FALLBACK.sentry_frontend_dsn,
    environment: BUILD_FALLBACK.environment,
    release: BUILD_FALLBACK.release,
  });

  let cfg = null;
  try {
    cfg = await _fetchPublicConfigWithTimeout();
    // (2) Re-attempt Sentry init in case the fallback had an empty DSN and
    //     runtime provides one. utils/sentry.js is a no-op if already active.
    initSentry({
      dsn: cfg.sentry_frontend_dsn || BUILD_FALLBACK.sentry_frontend_dsn,
      environment: cfg.environment || BUILD_FALLBACK.environment,
      release: cfg.release || BUILD_FALLBACK.release,
    });
  } catch (e) {
    // (3) Runtime config failed — beacon the blind spot so we can measure how
    //     often this happens. Sentry is already initialised from the fallback
    //     above (step 1), so error capture continues normally.
    try {
      const payload = JSON.stringify({
        event: "config_public_fetch_failed",
        message: String(e && e.message ? e.message : e),
        href: window.location.href,
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${backendUrl}/api/client-log`, payload);
      } else {
        fetch(`${backendUrl}/api/client-log`, { method: "POST", body: payload, keepalive: true });
      }
    } catch {
      /* nothing more we can do */
    }
    // Use build-time fallback for the deferred chain too.
    cfg = {
      posthog_js_key: BUILD_FALLBACK.posthog_js_key,
      posthog_host: BUILD_FALLBACK.posthog_host,
    };
  }

  // (4) Deferred: Replay integration + PostHog SDK.
  _scheduleDeferred(cfg);
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
